<template>
  <article>
    <LoaderStack v-if="isLoading" />
    <template v-else>
      <AreaHelpTechnicalSupportBanner />
      <div class="h-full w-full tablet:flex">
        <div class="flex w-full justify-end tablet:w-1/2">
          <div class="section w-full px-3 py-12 desktop:px-0 desktop:pr-12">
            <AreaHelpTechnicalSupportForm />
          </div>
        </div>
        <div class="flex w-full justify-start tablet:w-1/2">
          <div class="section w-full px-3 py-12 desktop:px-0 desktop:pl-12">
            <AreaHelpTechnicalSupportInfo />
          </div>
        </div>
      </div>
      <PopUpFormSuccess />
      <PopUpFormWarning />
    </template>
  </article>
</template>

<script setup lang="ts">
  const { t } = useI18n({ useScope: 'local' });
  const mainStore = useMainStore();
  const technicalSupportStore = useTechnicalSupportStore();
  const { provideWebTitle } = useWebsite();

  const { status: GET_COUNTRIES_STATUS } = await mainStore.USE_GET_COUNTRIES();
  const { status: GET_MEMBER_COUNTRIES_STATUS } =
    await mainStore.USE_GET_MEMBER_COUNTRIES();
  const { status: GET_ISSUE_TYPES_STATUS } =
    await technicalSupportStore.USE_GET_ISSUE_TYPES();

  const isLoading = computed(
    (): boolean =>
      GET_COUNTRIES_STATUS.value === 'pending' ||
      GET_MEMBER_COUNTRIES_STATUS.value === 'pending' ||
      GET_ISSUE_TYPES_STATUS.value === 'pending'
  );

  useSeoMeta({
    title: () => t('seo-title'),
    ogTitle: () => provideWebTitle(t('seo-title')),
    twitterTitle: () => provideWebTitle(t('seo-title')),
    description: () => t('seo-description'),
    ogDescription: () => t('seo-description'),
    twitterDescription: () => t('seo-description'),
  });
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/_utils.scss' as *;

  .section {
    @include min-width($desktop-min-size) {
      max-width: 45vw;
    }

    @include min-width(1776px) {
      max-width: 800px;
    }
  }
</style>

<i18n lang="json">
{
  "en-int": {
    "seo-title": "Technical Support",
    "seo-description": "Access expert technical support from WISE-Marketplace to optimize your IoT operations and resolve any issues."
  },
  "en-jp": {
    "seo-title": "Technical Support",
    "seo-description": "Access expert technical support from WISE-Marketplace to optimize your IoT operations and resolve any issues."
  },
  "en-tw": {
    "seo-title": "Technical Support",
    "seo-description": "Access expert technical support from WISE-Marketplace to optimize your IoT operations and resolve any issues."
  },
  "en-kr": {
    "seo-title": "Technical Support",
    "seo-description": "Access expert technical support from WISE-Marketplace to optimize your IoT operations and resolve any issues."
  },
  "en-americas": {
    "seo-title": "Technical Support",
    "seo-description": "Access expert technical support from WISE-Marketplace to optimize your IoT operations and resolve any issues."
  },
  "en-asia-pacific": {
    "seo-title": "Technical Support",
    "seo-description": "Access expert technical support from WISE-Marketplace to optimize your IoT operations and resolve any issues."
  },
  "en-emea": {
    "seo-title": "Technical Support",
    "seo-description": "Access expert technical support from WISE-Marketplace to optimize your IoT operations and resolve any issues."
  },
  "ja-jp": {
    "seo-title": "技術サポ",
    "seo-description": "WISE-Marketplace の技術サポートを活用して、課題解決や IoT 運用の最適化を実現しましょう。"
  },
  "zh-cn": {
    "seo-title": "技术支持",
    "seo-description": "面临技术难题？WISE-Marketplace 提供专业技术支持，帮助解决问题并优化 IoT 系统运行效率。"
  },
  "zh-tw": {
    "seo-title": "技術支援",
    "seo-description": "面臨技術挑戰？WISE-Marketplace 提供專業技術支援，助您解決問題並優化 IoT 系統效能。"
  }
}
</i18n>
